import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_ENDPOINTS, API_BASE_URL } from '../../config/api';
import { useToast } from '../../components/ToastProvider';

const reservationsListUrl = (restaurantId, queryString) =>
    `${API_BASE_URL}/reservations/restaurant/${restaurantId}${queryString ? `?${queryString}` : ''}`;
const reservationsPendingCountUrl = (restaurantId) =>
    `${API_BASE_URL}/reservations/restaurant/${restaurantId}/pending-count`;
const RESERVATIONS_URL = `${API_BASE_URL}/reservations`;
const reservationByIdUrl = (id) => `${API_BASE_URL}/reservations/${id}`;

const STATUS_OPTIONS = [
    { value: 'pending', label: 'Pending', color: 'bg-amber-100 text-amber-800 border-amber-200' },
    { value: 'confirmed', label: 'Confirmed', color: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
    { value: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-800 border-red-200' },
    { value: 'completed', label: 'Completed', color: 'bg-slate-100 text-slate-800 border-slate-200' },
];

const OCCASIONS = [
    '',
    'Birthday Celebration',
    'Marriage Anniversary',
    'Family / Friends Party',
    'Business Dinner',
    'Romantic Dinner',
    'Other',
];

const DATE_FILTERS = [
    { value: 'all', label: 'All' },
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'week', label: 'This week' },
    { value: 'month', label: 'This month' },
];

const REMINDER_OPTIONS = [
    { value: 30, label: '30 minutes before' },
    { value: 60, label: '1 hour before' },
    { value: 120, label: '2 hours before' },
];

function getDateRange(filter) {
    const now = new Date();
    const y = now.getFullYear(), m = now.getMonth(), d = now.getDate();
    const pad = (n) => String(n).padStart(2, '0');
    if (filter === 'today') {
        const from = `${y}-${pad(m + 1)}-${pad(d)}`;
        return { fromDate: from, toDate: from };
    }
    if (filter === 'yesterday') {
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        const fy = yesterday.getFullYear(), fm = yesterday.getMonth(), fd = yesterday.getDate();
        const from = `${fy}-${pad(fm + 1)}-${pad(fd)}`;
        return { fromDate: from, toDate: from };
    }
    if (filter === 'week') {
        const day = now.getDay();
        const mondayOffset = day === 0 ? -6 : 1 - day;
        const start = new Date(now);
        start.setDate(d + mondayOffset);
        const end = new Date(start);
        end.setDate(end.getDate() + 6);
        const sy = start.getFullYear(), sm = start.getMonth(), sd = start.getDate();
        const ey = end.getFullYear(), em = end.getMonth(), ed = end.getDate();
        return { fromDate: `${sy}-${pad(sm + 1)}-${pad(sd)}`, toDate: `${ey}-${pad(em + 1)}-${pad(ed)}` };
    }
    if (filter === 'month') {
        const from = `${y}-${pad(m + 1)}-01`;
        const lastDay = new Date(y, m + 1, 0).getDate();
        const to = `${y}-${pad(m + 1)}-${pad(lastDay)}`;
        return { fromDate: from, toDate: to };
    }
    return { fromDate: '', toDate: '' };
}

// Compute effective table status for assign-table dropdown: if another reservation
// (same date+time, not cancelled) already has this table, show "Reserved (this slot)".
function getTableStatusForSlot(tables, reservations, contextDate, contextTime, excludeReservationId) {
    const normalDate = (d) => {
        if (!d) return '';
        if (typeof d === 'string' && d.length >= 10) return d.slice(0, 10);
        try { return new Date(d).toISOString().slice(0, 10); } catch { return ''; }
    };
    const ctxDate = normalDate(contextDate);
    const ctxTime = (contextTime || '').trim();
    const statusByTableId = {};
    (tables || []).forEach((t) => {
        const baseStatus = t.status || 'Available';
        const hasConflict = (reservations || []).some((r) => {
            if (excludeReservationId && r._id === excludeReservationId) return false;
            if (r.status === 'cancelled') return false;
            const rTableId = r.assignedTableId?._id || r.assignedTableId;
            if (!rTableId || String(rTableId) !== String(t._id)) return false;
            const rDate = normalDate(r.reservationDate);
            const rTime = (r.reservationTime || '').trim();
            return rDate === ctxDate && rTime === ctxTime;
        });
        statusByTableId[t._id] = hasConflict ? 'Reserved' : baseStatus;
    });
    return statusByTableId;
}

const DineInSection = ({ restaurantId }) => {
    const addToast = useToast();
    const [reservations, setReservations] = useState([]);
    const [tables, setTables] = useState([]);
    const [menuItems, setMenuItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('');
    const [dateFilter, setDateFilter] = useState('today');
    const [selected, setSelected] = useState(null);
    const [viewing, setViewing] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [editForm, setEditForm] = useState({
        customerName: '',
        email: '',
        contactNumber: '',
        numberOfPeople: 1,
        reservationDate: '',
        reservationTime: '',
        occasion: '',
        message: '',
        specialInstructions: '',
        selectedItems: [],
        status: 'pending',
        assignedTableId: '',
        restaurantNotes: '',
        sendReminder: false,
        reminderMinutesBefore: 30,
    });
    const [saving, setSaving] = useState(false);
    const [pendingCount, setPendingCount] = useState(0);
    const [isFullScreen, setIsFullScreen] = useState(false);

    // Create reservation (owner books for customer)
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createSaving, setCreateSaving] = useState(false);
    const [createError, setCreateError] = useState('');
    const [createForm, setCreateForm] = useState({
        customerName: '',
        email: '',
        contactNumber: '',
        numberOfPeople: 1,
        reservationDate: '',
        reservationTime: '',
        occasion: '',
        message: '',
        specialInstructions: '',
        selectedItems: [],
        assignedTableId: '',
        status: 'pending',
        sendReminder: false,
        reminderMinutesBefore: 30,
    });

    const fetchReservations = async () => {
        if (!restaurantId) return;
        try {
            const params = [];
            if (statusFilter) params.push(`status=${encodeURIComponent(statusFilter)}`);
            const { fromDate, toDate } = getDateRange(dateFilter);
            if (fromDate) params.push(`fromDate=${encodeURIComponent(fromDate)}`);
            if (toDate) params.push(`toDate=${encodeURIComponent(toDate)}`);
            const query = params.join('&');
            const url = reservationsListUrl(restaurantId, query);
            const res = await axios.get(url);
            setReservations(Array.isArray(res.data) ? res.data : []);
        } catch (e) {
            console.error('Failed to fetch reservations', e);
            setReservations([]);
        }
    };

    const fetchTables = async () => {
        if (!restaurantId) return;
        try {
            const res = await axios.get(API_ENDPOINTS.TABLES(restaurantId));
            setTables(Array.isArray(res.data) ? res.data : []);
        } catch (e) {
            console.error('Failed to fetch tables', e);
            setTables([]);
        }
    };

    const fetchPendingCount = async () => {
        if (!restaurantId) return;
        try {
            const url = reservationsPendingCountUrl(restaurantId);
            const res = await axios.get(url);
            setPendingCount(res.data?.count ?? 0);
        } catch {
            setPendingCount(0);
        }
    };

    useEffect(() => {
        if (!restaurantId) return;
        setLoading(true);
        Promise.all([fetchReservations(), fetchTables(), fetchPendingCount()]).finally(() =>
            setLoading(false)
        );
    }, [restaurantId, statusFilter, dateFilter]);

    const openEdit = async (reservation) => {
        setSelected(reservation);
        const dateStr = reservation.reservationDate
            ? new Date(reservation.reservationDate).toISOString().slice(0, 10)
            : '';
        setEditForm({
            customerName: reservation.customerName || '',
            email: reservation.email || '',
            contactNumber: reservation.contactNumber || '',
            numberOfPeople: reservation.numberOfPeople || 1,
            reservationDate: dateStr,
            reservationTime: reservation.reservationTime || '',
            occasion: reservation.occasion || '',
            message: reservation.message || '',
            specialInstructions: reservation.specialInstructions || '',
            selectedItems: Array.isArray(reservation.selectedItems) ? [...reservation.selectedItems] : [],
            status: reservation.status || 'pending',
            assignedTableId: reservation.assignedTableId?._id || reservation.assignedTableId || '',
            restaurantNotes: reservation.restaurantNotes || '',
            sendReminder: !!reservation.sendReminder,
            reminderMinutesBefore: reservation.reminderMinutesBefore ?? 30,
        });
        if (restaurantId && menuItems.length === 0) {
            try {
                const res = await axios.get(`${API_ENDPOINTS.MENU}?restaurantId=${restaurantId}`);
                setMenuItems(Array.isArray(res.data) ? res.data.filter((m) => m.isVisible !== false) : []);
            } catch {
                setMenuItems([]);
            }
        }
    };

    const openCreateModal = async () => {
        setCreateError('');
        setCreateForm({
            customerName: '',
            email: '',
            contactNumber: '',
            numberOfPeople: 1,
            reservationDate: '',
            reservationTime: '',
            occasion: '',
            message: '',
            specialInstructions: '',
            selectedItems: [],
            assignedTableId: '',
            status: 'pending',
            sendReminder: false,
            reminderMinutesBefore: 30,
        });
        setShowCreateModal(true);
        if (restaurantId && menuItems.length === 0) {
            try {
                const res = await axios.get(`${API_ENDPOINTS.MENU}?restaurantId=${restaurantId}`);
                setMenuItems(Array.isArray(res.data) ? res.data.filter((m) => m.isVisible !== false) : []);
            } catch {
                setMenuItems([]);
            }
        }
    };

    const addCreateMenuItem = (item) => {
        setCreateForm((prev) => {
            const existing = prev.selectedItems.find((i) => i.menuItemId === item._id);
            if (existing) {
                return {
                    ...prev,
                    selectedItems: prev.selectedItems.map((i) =>
                        i.menuItemId === item._id ? { ...i, quantity: i.quantity + 1 } : i
                    ),
                };
            }
            return {
                ...prev,
                selectedItems: [
                    ...prev.selectedItems,
                    { menuItemId: item._id, name: item.itemName, price: item.itemPrice, quantity: 1 },
                ],
            };
        });
    };

    const removeCreateMenuItem = (menuItemId) => {
        setCreateForm((prev) => ({
            ...prev,
            selectedItems: prev.selectedItems.filter((i) => i.menuItemId !== menuItemId),
        }));
    };

    const updateCreateItemQty = (menuItemId, delta) => {
        setCreateForm((prev) => ({
            ...prev,
            selectedItems: prev.selectedItems
                .map((i) =>
                    i.menuItemId === menuItemId ? { ...i, quantity: Math.max(1, i.quantity + delta) } : i
                )
                .filter((i) => i.quantity > 0),
        }));
    };

    const handleCreateSubmit = async () => {
        setCreateError('');
        if (!createForm.customerName?.trim() || !createForm.email?.trim() || !createForm.contactNumber?.trim()) {
            setCreateError('Please enter customer name, email and contact number.');
            return;
        }
        if (!/^\d{10}$/.test(createForm.contactNumber.trim())) {
            setCreateError('Contact number must be exactly 10 digits.');
            return;
        }
        if (!createForm.reservationDate || !createForm.reservationTime) {
            setCreateError('Please select date and time.');
            return;
        }
        setCreateSaving(true);
        try {
            const payload = {
                restaurantId,
                customerName: createForm.customerName.trim(),
                email: createForm.email.trim(),
                contactNumber: createForm.contactNumber.trim(),
                numberOfPeople: Math.max(1, parseInt(createForm.numberOfPeople, 10) || 1),
                selectedItems: createForm.selectedItems,
                reservationDate: createForm.reservationDate,
                reservationTime: createForm.reservationTime,
                occasion: createForm.occasion.trim(),
                message: createForm.message.trim(),
                specialInstructions: createForm.specialInstructions.trim(),
                sendReminder: !!createForm.sendReminder,
                reminderMinutesBefore: createForm.sendReminder ? (createForm.reminderMinutesBefore || 30) : 30,
            };
            const res = await axios.post(RESERVATIONS_URL, payload);
            const createdId = res.data?._id;
            if (createdId && (createForm.assignedTableId || createForm.status !== 'pending' || createForm.sendReminder)) {
                await axios.put(reservationByIdUrl(createdId), {
                    status: createForm.status,
                    assignedTableId: createForm.assignedTableId || null,
                    sendReminder: !!createForm.sendReminder,
                    reminderMinutesBefore: createForm.sendReminder ? (createForm.reminderMinutesBefore || 30) : 30,
                });
            }
            await fetchReservations();
            await fetchPendingCount();
            setShowCreateModal(false);
        } catch (e) {
            setCreateError(e.response?.data?.message || 'Failed to create reservation. Please try again.');
        } finally {
            setCreateSaving(false);
        }
    };

    const handleSave = async () => {
        if (!selected?._id) {
            addToast('No reservation selected.', 'error');
            return;
        }
        if (!editForm.customerName?.trim() || !editForm.email?.trim() || !editForm.contactNumber?.trim()) {
            addToast('Please fill in customer name, email, and contact number.', 'error');
            return;
        }
        if (!/^\d{10}$/.test(editForm.contactNumber.trim())) {
            addToast('Contact number must be exactly 10 digits.', 'error');
            return;
        }
        if (!editForm.reservationDate || !editForm.reservationTime) {
            addToast('Please select reservation date and time.', 'error');
            return;
        }
        setSaving(true);
        try {
            const selectedItems = (editForm.selectedItems || []).map((i) => ({
                menuItemId: typeof i.menuItemId === 'object' ? i.menuItemId?._id ?? i.menuItemId : i.menuItemId,
                name: i.name,
                price: Number(i.price),
                quantity: Number(i.quantity) || 1,
            }));
            await axios.put(reservationByIdUrl(selected._id), {
                customerName: editForm.customerName.trim(),
                email: editForm.email.trim(),
                contactNumber: editForm.contactNumber.trim(),
                numberOfPeople: Math.max(1, parseInt(editForm.numberOfPeople, 10) || 1),
                reservationDate: editForm.reservationDate,
                reservationTime: editForm.reservationTime,
                occasion: (editForm.occasion || '').trim(),
                message: (editForm.message || '').trim(),
                specialInstructions: (editForm.specialInstructions || '').trim(),
                selectedItems,
                status: editForm.status,
                assignedTableId: editForm.assignedTableId || null,
                restaurantNotes: (editForm.restaurantNotes || '').trim(),
                sendReminder: !!editForm.sendReminder,
                reminderMinutesBefore: editForm.sendReminder ? (editForm.reminderMinutesBefore || 30) : 30,
            });
            await fetchReservations();
            await fetchPendingCount();
            setSelected(null);
            addToast('Reservation updated successfully!', 'success');
        } catch (e) {
            console.error('Failed to update reservation', e);
            addToast(e.response?.data?.message || 'Failed to update reservation', 'error');
        } finally {
            setSaving(false);
        }
    };

    const addEditMenuItem = (item) => {
        setEditForm((prev) => {
            const existing = prev.selectedItems.find((i) => i.menuItemId === item._id);
            if (existing) {
                return {
                    ...prev,
                    selectedItems: prev.selectedItems.map((i) =>
                        i.menuItemId === item._id ? { ...i, quantity: i.quantity + 1 } : i
                    ),
                };
            }
            return {
                ...prev,
                selectedItems: [
                    ...prev.selectedItems,
                    { menuItemId: item._id, name: item.itemName, price: item.itemPrice, quantity: 1 },
                ],
            };
        });
    };

    const removeEditMenuItem = (menuItemId) => {
        setEditForm((prev) => ({
            ...prev,
            selectedItems: prev.selectedItems.filter((i) => i.menuItemId !== menuItemId),
        }));
    };

    const updateEditItemQty = (menuItemId, delta) => {
        setEditForm((prev) => ({
            ...prev,
            selectedItems: prev.selectedItems
                .map((i) =>
                    i.menuItemId === menuItemId ? { ...i, quantity: Math.max(1, i.quantity + delta) } : i
                )
                .filter((i) => i.quantity > 0),
        }));
    };

    const openDeleteModal = (reservation) => {
        setDeleteTarget(reservation);
        if (selected?._id === reservation?._id) setSelected(null);
    };

    const handleConfirmDelete = async () => {
        if (!deleteTarget?._id) return;
        setDeleting(true);
        try {
            await axios.delete(reservationByIdUrl(deleteTarget._id));
            await fetchReservations();
            await fetchPendingCount();
            setDeleteTarget(null);
            addToast('Reservation deleted.', 'success');
        } catch (e) {
            console.error('Failed to delete reservation', e);
            addToast(e.response?.data?.message || 'Failed to delete reservation.', 'error');
        } finally {
            setDeleting(false);
        }
    };

    const formatDate = (d) => {
        if (!d) return '—';
        const date = new Date(d);
        return date.toLocaleDateString('en-IN', {
            weekday: 'short',
            day: 'numeric',
            month: 'short',
            year: 'numeric',
        });
    };

    const formatDateTime = (d) => {
        if (!d) return '—';
        const date = new Date(d);
        const dateStr = date.toLocaleDateString('en-IN', {
            weekday: 'short',
            day: 'numeric',
            month: 'short',
            year: 'numeric',
        });
        const timeStr = date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false });
        return `${dateStr} · ${timeStr}`;
    };

    const getStatusStyle = (status) => {
        return STATUS_OPTIONS.find((s) => s.value === status)?.color || 'bg-stone-100 text-stone-700 border-stone-200';
    };

    if (!restaurantId) return null;

    const toolbar = (
        <div className="flex flex-col gap-3 mb-4 mx-3 sm:mx-0">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                    <p className="text-base font-bold text-stone-800">
                        Dine-in reservations
                    </p>
                    <p className="text-sm text-stone-600 mt-0.5 truncate">
                        {pendingCount > 0 && (
                            <span className="text-amber-600 font-medium">{pendingCount} pending</span>
                        )}
                        {pendingCount === 0 && reservations.length === 0 && 'No reservations yet.'}
                        {pendingCount === 0 && reservations.length > 0 && `${reservations.length} reservation(s).`}
                    </p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                    <button
                        type="button"
                        onClick={() => setIsFullScreen((v) => !v)}
                        className="p-2 rounded-lg border border-stone-200 bg-white text-stone-600 hover:bg-stone-50 hover:text-stone-800 transition-colors"
                        title={isFullScreen ? 'Exit full screen' : 'Full screen'}
                        aria-label={isFullScreen ? 'Exit full screen' : 'Full screen'}
                    >
                        {isFullScreen ? (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        ) : (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                            </svg>
                        )}
                    </button>
                    <button
                        type="button"
                        onClick={openCreateModal}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium transition-colors shrink-0"
                    >
                        <span>➕</span>
                        <span className="hidden sm:inline">Book table</span>
                    </button>
                </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-stone-500 sr-only sm:not-sr-only">Period:</span>
                <select
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                    className="border border-stone-200 rounded-lg px-3 py-2 text-sm text-stone-700 bg-white min-w-0 max-w-[140px] sm:max-w-none"
                >
                    {DATE_FILTERS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                            {opt.label}
                        </option>
                    ))}
                </select>
                <span className="text-xs text-stone-500 sr-only sm:not-sr-only">Status:</span>
                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="border border-stone-200 rounded-lg px-3 py-2 text-sm text-stone-700 bg-white min-w-0 max-w-[120px] sm:max-w-none"
                >
                    <option value="">All</option>
                    {STATUS_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                            {opt.label}
                        </option>
                    ))}
                </select>
                <button
                    type="button"
                    onClick={() => {
                        setStatusFilter('');
                        setDateFilter('all');
                        fetchReservations();
                        fetchPendingCount();
                    }}
                    className="text-xs text-orange-600 hover:text-orange-700 font-medium py-2"
                >
                    Refresh
                </button>
            </div>
        </div>
    );

    const listContent = (
        <>
            {loading ? (
                <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-orange-500 border-t-transparent" />
                </div>
            ) : reservations.length === 0 ? (
                <div className="text-center py-12 bg-stone-50 rounded-xl border border-stone-200">
                    <div className="text-4xl mb-2">🪑</div>
                    <p className="text-stone-600 font-medium">No reservations</p>
                    <p className="text-sm text-stone-500 mt-1 px-2">
                        Table reservation requests from customers will appear here.
                    </p>
                </div>
            ) : (
                <>
                    {/* Mobile: card list */}
                    <div className="md:hidden space-y-3 mx-3">
                        {reservations.map((r) => (
                            <div
                                key={r._id}
                                className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm"
                            >
                                <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                                    <p className="font-medium text-stone-800 truncate flex-1 min-w-0">{r.customerName}</p>
                                    <span className={`shrink-0 text-xs px-2 py-0.5 rounded-full border ${getStatusStyle(r.status)}`}>
                                        {r.status}
                                    </span>
                                </div>
                                <div className="text-xs text-stone-500 space-y-0.5 mb-3">
                                    <p>{formatDate(r.reservationDate)} · {r.reservationTime}</p>
                                    <p>
                                        {r.assignedTableId
                                            ? (r.assignedTableId?.tableNumber || r.assignedTableId?.tableName || r.assignedTableId)
                                            : 'No table'}
                                        {' · '}{r.numberOfPeople} guest{r.numberOfPeople !== 1 ? 's' : ''}
                                    </p>
                                    {r.contactNumber && <p>{r.contactNumber}</p>}
                                    {r.createdAt && (
                                        <p className="text-stone-400">Created: {formatDateTime(r.createdAt)}</p>
                                    )}
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setViewing(r)}
                                        className="text-xs font-medium text-stone-600 hover:text-stone-800 border border-stone-200 px-2.5 py-1.5 rounded-lg"
                                    >
                                        View
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => openEdit(r)}
                                        className="text-xs font-medium text-orange-600 hover:text-orange-700 border border-orange-200 px-2.5 py-1.5 rounded-lg"
                                    >
                                        Edit
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => openDeleteModal(r)}
                                        className="text-xs font-medium text-red-600 hover:text-red-700 border border-red-200 px-2.5 py-1.5 rounded-lg"
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                    {/* Desktop: table */}
                    <div className="hidden md:block overflow-x-auto rounded-xl border border-stone-200 bg-white -webkit-overflow-scrolling-touch">
                        <table className="w-full min-w-[900px] text-left text-sm">
                            <thead>
                                <tr className="border-b border-stone-200 bg-stone-50">
                                    <th className="px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-stone-500 whitespace-nowrap">Customer</th>
                                    <th className="px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-stone-500 whitespace-nowrap">Status</th>
                                    <th className="px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-stone-500 whitespace-nowrap">Table</th>
                                    <th className="px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-stone-500 whitespace-nowrap">Email</th>
                                    <th className="px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-stone-500 whitespace-nowrap">Contact</th>
                                    <th className="px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-stone-500 whitespace-nowrap">Date</th>
                                    <th className="px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-stone-500 whitespace-nowrap">Time</th>
                                    <th className="px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-stone-500 whitespace-nowrap">Guests</th>
                                    <th className="px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-stone-500 whitespace-nowrap">Occasion</th>
                                    <th className="px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-stone-500 whitespace-nowrap">Message / Instructions</th>
                                    <th className="px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-stone-500 whitespace-nowrap">Pre-order</th>
                                    <th className="px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-stone-500 whitespace-nowrap">Created</th>
                                    <th className="px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-stone-500 whitespace-nowrap text-right">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reservations.map((r) => (
                                    <tr
                                        key={r._id}
                                        className="border-b border-stone-100 hover:bg-stone-50/80 transition-colors"
                                    >
                                        <td className="px-3 py-2.5 font-medium text-stone-800 whitespace-nowrap">
                                            {r.customerName}
                                        </td>
                                        <td className="px-3 py-2.5">
                                            <span className={`inline-block text-xs px-2 py-0.5 rounded-full border ${getStatusStyle(r.status)}`}>
                                                {r.status}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2.5 text-stone-600 whitespace-nowrap">
                                            {r.assignedTableId
                                                ? (r.assignedTableId?.tableNumber || r.assignedTableId?.tableName || r.assignedTableId)
                                                : '—'}
                                        </td>
                                        <td className="px-3 py-2.5 text-stone-600 whitespace-nowrap max-w-[140px] truncate" title={r.email}>
                                            {r.email}
                                        </td>
                                        <td className="px-3 py-2.5 text-stone-600 whitespace-nowrap">
                                            {r.contactNumber}
                                        </td>
                                        <td className="px-3 py-2.5 text-stone-600 whitespace-nowrap">
                                            {formatDate(r.reservationDate)}
                                        </td>
                                        <td className="px-3 py-2.5 text-stone-600 whitespace-nowrap">
                                            {r.reservationTime}
                                        </td>
                                        <td className="px-3 py-2.5 text-stone-600 whitespace-nowrap">
                                            {r.numberOfPeople}
                                        </td>
                                        <td className="px-3 py-2.5 text-stone-600 whitespace-nowrap max-w-[120px] truncate" title={r.occasion || ''}>
                                            {r.occasion || '—'}
                                        </td>
                                        <td className="px-3 py-2.5 text-stone-600 max-w-[180px] truncate" title={[r.message, r.specialInstructions].filter(Boolean).join(' · ') || ''}>
                                            {[r.message, r.specialInstructions].filter(Boolean).join(' · ') || '—'}
                                        </td>
                                        <td className="px-3 py-2.5 text-stone-600 max-w-[160px] truncate" title={r.selectedItems?.length ? r.selectedItems.map((i) => `${i.name}×${i.quantity}`).join(', ') : ''}>
                                            {r.selectedItems?.length > 0
                                                ? r.selectedItems.map((i) => `${i.name}×${i.quantity}`).join(', ')
                                                : '—'}
                                        </td>
                                        <td className="px-3 py-2.5 text-stone-600 whitespace-nowrap text-xs">
                                            {r.createdAt ? formatDateTime(r.createdAt) : '—'}
                                        </td>
                                        <td className="px-3 py-2.5 text-right whitespace-nowrap">
                                            <div className="flex items-center justify-end gap-1 flex-wrap">
                                                <button
                                                    type="button"
                                                    onClick={() => setViewing(r)}
                                                    className="text-sm font-medium text-stone-600 hover:text-stone-800 border border-stone-200 hover:border-stone-300 px-2.5 py-1 rounded-lg"
                                                    title="View details"
                                                >
                                                    View
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => openEdit(r)}
                                                    className="text-sm font-medium text-orange-600 hover:text-orange-700 border border-orange-200 hover:border-orange-300 px-2.5 py-1 rounded-lg"
                                                    title="Edit"
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => openDeleteModal(r)}
                                                    className="text-sm font-medium text-red-600 hover:text-red-700 border border-red-200 hover:border-red-300 px-2.5 py-1 rounded-lg"
                                                    title="Delete"
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </>
    );

    const mainInner = (
        <div className="w-full min-h-0">
            {toolbar}
            {listContent}
        </div>
    );

    return (
        <div className="w-full">
            {isFullScreen ? (
                <div className="fixed inset-0 z-[100] bg-white flex flex-col overflow-hidden">
                    <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-stone-200 bg-stone-50 shrink-0">
                        <p className="text-sm font-semibold text-stone-800">Dine-in Reservations</p>
                        <button
                            type="button"
                            onClick={() => setIsFullScreen(false)}
                            className="p-2 rounded-lg border border-stone-200 bg-white text-stone-600 hover:bg-stone-100 font-medium text-sm"
                        >
                            Exit full screen
                        </button>
                    </div>
                    <div className="flex-1 min-h-0 overflow-y-auto p-4">
                        {mainInner}
                    </div>
                </div>
            ) : (
                mainInner
            )}

            {/* View details modal (read-only) */}
            {viewing && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/60 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-xl shadow-2xl border border-stone-200 max-w-lg w-full max-h-[90vh] flex flex-col overflow-hidden">
                        <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 border-b border-stone-200 bg-stone-50">
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500">
                                Reservation details
                            </p>
                            <button
                                type="button"
                                onClick={() => setViewing(null)}
                                className="w-7 h-7 rounded-full border border-stone-300 flex items-center justify-center text-stone-600 hover:bg-stone-100"
                            >
                                ×
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-4 space-y-4">
                            <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                                <div>
                                    <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-400">Customer</p>
                                    <p className="text-stone-800 font-medium">{viewing.customerName}</p>
                                </div>
                                <div>
                                    <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-400">Status</p>
                                    <span className={`inline-block text-xs px-2 py-0.5 rounded-full border ${getStatusStyle(viewing.status)}`}>
                                        {viewing.status}
                                    </span>
                                </div>
                                <div>
                                    <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-400">Email</p>
                                    <p className="text-stone-700">{viewing.email}</p>
                                </div>
                                <div>
                                    <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-400">Contact</p>
                                    <p className="text-stone-700">{viewing.contactNumber}</p>
                                </div>
                                <div>
                                    <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-400">Date & time</p>
                                    <p className="text-stone-700">{formatDate(viewing.reservationDate)} at {viewing.reservationTime}</p>
                                </div>
                                <div>
                                    <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-400">Guests</p>
                                    <p className="text-stone-700">{viewing.numberOfPeople}</p>
                                </div>
                                {viewing.createdAt && (
                                    <div className="col-span-2">
                                        <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-400">Created</p>
                                        <p className="text-stone-700">{formatDateTime(viewing.createdAt)}</p>
                                    </div>
                                )}
                                <div>
                                    <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-400">Assigned table</p>
                                    <p className="text-stone-700">
                                        {viewing.assignedTableId
                                            ? (viewing.assignedTableId?.tableNumber || viewing.assignedTableId?.tableName || '—')
                                            : '—'}
                                    </p>
                                </div>
                                {viewing.occasion && (
                                    <div className="col-span-2">
                                        <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-400">Occasion</p>
                                        <p className="text-stone-700">{viewing.occasion}</p>
                                    </div>
                                )}
                                {(viewing.message || viewing.specialInstructions) && (
                                    <div className="col-span-2">
                                        <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-400">Message / Instructions</p>
                                        <p className="text-stone-700">{[viewing.message, viewing.specialInstructions].filter(Boolean).join(' · ') || '—'}</p>
                                    </div>
                                )}
                                {viewing.selectedItems?.length > 0 && (
                                    <div className="col-span-2">
                                        <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-400">Pre-order</p>
                                        <p className="text-stone-700">{viewing.selectedItems.map((i) => `${i.name} × ${i.quantity}`).join(', ')}</p>
                                    </div>
                                )}
                                {viewing.restaurantNotes && (
                                    <div className="col-span-2">
                                        <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-400">Your notes</p>
                                        <p className="text-stone-700">{viewing.restaurantNotes}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="sticky bottom-0 flex gap-2 p-4 border-t border-stone-200 bg-white">
                            <button
                                type="button"
                                onClick={() => setViewing(null)}
                                className="flex-1 py-2.5 rounded-lg border border-stone-200 text-stone-700 font-medium"
                            >
                                Close
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setViewing(null);
                                    openEdit(viewing);
                                }}
                                className="flex-1 py-2.5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white font-medium"
                            >
                                Edit
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete reservation confirmation modal */}
            {deleteTarget && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/60 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-xl shadow-2xl border border-stone-200 max-w-md w-full overflow-hidden">
                        <div className="p-4 sm:p-5">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                                    <span className="text-xl" aria-hidden="true">🗑️</span>
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-stone-800">Delete reservation?</h3>
                                    <p className="text-sm text-stone-500">This action cannot be undone.</p>
                                </div>
                            </div>
                            <div className="bg-stone-50 border border-stone-200 rounded-lg p-3 space-y-1.5 text-sm">
                                <p className="font-medium text-stone-800">{deleteTarget.customerName}</p>
                                <p className="text-stone-600">
                                    {formatDate(deleteTarget.reservationDate)} at {deleteTarget.reservationTime}
                                    {' · '}{deleteTarget.numberOfPeople} guest{deleteTarget.numberOfPeople !== 1 ? 's' : ''}
                                </p>
                                {deleteTarget.contactNumber && (
                                    <p className="text-stone-500">{deleteTarget.contactNumber}</p>
                                )}
                            </div>
                        </div>
                        <div className="flex gap-3 p-4 border-t border-stone-200 bg-stone-50">
                            <button
                                type="button"
                                onClick={() => setDeleteTarget(null)}
                                disabled={deleting}
                                className="flex-1 py-2.5 rounded-lg border border-stone-200 bg-white text-stone-700 font-medium hover:bg-stone-50 disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleConfirmDelete}
                                disabled={deleting}
                                className="flex-1 py-2.5 rounded-lg bg-red-500 hover:bg-red-600 text-white font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {deleting ? (
                                    <>
                                        <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                                        Deleting...
                                    </>
                                ) : (
                                    'Delete'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit modal - full customer details editable */}
            {selected && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/60 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-xl shadow-2xl border border-stone-200 max-w-lg w-full max-h-[90vh] flex flex-col overflow-hidden">
                        <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 border-b border-stone-200 bg-stone-50">
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500">
                                Edit reservation
                            </p>
                            <button
                                type="button"
                                onClick={() => setSelected(null)}
                                className="w-7 h-7 rounded-full border border-stone-300 flex items-center justify-center text-stone-600 hover:bg-stone-100"
                            >
                                ×
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-4 space-y-3">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1">Customer name *</label>
                                    <input
                                        type="text"
                                        value={editForm.customerName}
                                        onChange={(e) => setEditForm((f) => ({ ...f, customerName: e.target.value }))}
                                        className="w-full border border-stone-200 rounded-lg px-3 py-2 text-stone-800"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1">Email *</label>
                                    <input
                                        type="email"
                                        value={editForm.email}
                                        onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
                                        className="w-full border border-stone-200 rounded-lg px-3 py-2 text-stone-800"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1">Contact number * (10 digits)</label>
                                <input
                                    type="tel"
                                    value={editForm.contactNumber}
                                    onChange={(e) => {
                                        const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, 10);
                                        setEditForm((f) => ({ ...f, contactNumber: digitsOnly }));
                                    }}
                                    maxLength={10}
                                    inputMode="numeric"
                                    className="w-full border border-stone-200 rounded-lg px-3 py-2 text-stone-800"
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1">Number of guests *</label>
                                <input
                                    type="number"
                                    min={1}
                                    max={100}
                                    value={editForm.numberOfPeople}
                                    onChange={(e) => setEditForm((f) => ({ ...f, numberOfPeople: e.target.value }))}
                                    className="w-full border border-stone-200 rounded-lg px-3 py-2 text-stone-800"
                                />
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1">Date *</label>
                                    <input
                                        type="date"
                                        value={editForm.reservationDate}
                                        onChange={(e) => setEditForm((f) => ({ ...f, reservationDate: e.target.value }))}
                                        className="w-full border border-stone-200 rounded-lg px-3 py-2 text-stone-800"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1">Time *</label>
                                    <input
                                        type="time"
                                        value={editForm.reservationTime}
                                        onChange={(e) => setEditForm((f) => ({ ...f, reservationTime: e.target.value }))}
                                        className="w-full border border-stone-200 rounded-lg px-3 py-2 text-stone-800"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1">Occasion</label>
                                <select
                                    value={editForm.occasion}
                                    onChange={(e) => setEditForm((f) => ({ ...f, occasion: e.target.value }))}
                                    className="w-full border border-stone-200 rounded-lg px-3 py-2 text-stone-800 bg-white"
                                >
                                    {OCCASIONS.map((opt) => (
                                        <option key={opt || 'none'} value={opt}>{opt || '— Select —'}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1">Message / notes</label>
                                <textarea
                                    value={editForm.message}
                                    onChange={(e) => setEditForm((f) => ({ ...f, message: e.target.value }))}
                                    rows={1}
                                    className="w-full border border-stone-200 rounded-lg px-3 py-2 text-stone-800"
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1">Special instructions</label>
                                <textarea
                                    value={editForm.specialInstructions}
                                    onChange={(e) => setEditForm((f) => ({ ...f, specialInstructions: e.target.value }))}
                                    rows={1}
                                    className="w-full border border-stone-200 rounded-lg px-3 py-2 text-stone-800"
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1">Pre-order</label>
                                <div className="border border-stone-200 rounded-lg overflow-hidden max-h-28 overflow-y-auto">
                                    <ul className="divide-y divide-stone-100">
                                        {menuItems.slice(0, 25).map((item) => (
                                            <li key={item._id} className="flex items-center justify-between px-3 py-1.5 hover:bg-stone-50">
                                                <span className="text-xs text-stone-700 truncate flex-1">{item.itemName} — ₹{item.itemPrice}</span>
                                                <button type="button" onClick={() => addEditMenuItem(item)} className="text-orange-600 text-xs font-medium">Add</button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                {editForm.selectedItems.length > 0 && (
                                    <div className="mt-2 p-2 bg-stone-50 rounded-lg border border-stone-200">
                                        {editForm.selectedItems.map((i) => (
                                            <div key={i.menuItemId} className="flex items-center justify-between text-xs py-0.5">
                                                <span className="text-stone-700">{i.name} × {i.quantity}</span>
                                                <div className="flex items-center gap-0.5">
                                                    <button type="button" onClick={() => updateEditItemQty(i.menuItemId, -1)} className="w-5 h-5 rounded border border-stone-300">−</button>
                                                    <button type="button" onClick={() => updateEditItemQty(i.menuItemId, 1)} className="w-5 h-5 rounded border border-stone-300">+</button>
                                                    <button type="button" onClick={() => removeEditMenuItem(i.menuItemId)} className="text-red-600 ml-1">Remove</button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1">Status</label>
                                    <select
                                        value={editForm.status}
                                        onChange={(e) => setEditForm((f) => ({ ...f, status: e.target.value }))}
                                        className="w-full border border-stone-200 rounded-lg px-3 py-2 text-stone-800"
                                    >
                                        {STATUS_OPTIONS.map((opt) => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1">Assign table</label>
                                    <select
                                        value={editForm.assignedTableId}
                                        onChange={(e) => setEditForm((f) => ({ ...f, assignedTableId: e.target.value }))}
                                        className="w-full border border-stone-200 rounded-lg px-3 py-2 text-stone-800"
                                    >
                                        <option value="">— None —</option>
                                        {(() => {
                                            const statusMap = getTableStatusForSlot(tables, reservations, editForm.reservationDate, editForm.reservationTime, selected?._id);
                                            return tables.map((t) => (
                                                <option key={t._id} value={t._id}>
                                                    {t.tableName || t.tableNumber} — {statusMap[t._id] ?? (t.status || 'Available')}
                                                </option>
                                            ));
                                        })()}
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="block text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1">Your notes</label>
                                <textarea
                                    value={editForm.restaurantNotes}
                                    onChange={(e) => setEditForm((f) => ({ ...f, restaurantNotes: e.target.value }))}
                                    rows={2}
                                    className="w-full border border-stone-200 rounded-lg px-3 py-2 text-stone-800"
                                    placeholder="Internal notes"
                                />
                            </div>
                            <div className="flex flex-wrap items-center gap-3">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={!!editForm.sendReminder}
                                        onChange={(e) => setEditForm((f) => ({ ...f, sendReminder: e.target.checked }))}
                                        className="rounded border-stone-300 text-orange-600 focus:ring-orange-500"
                                    />
                                    <span className="text-sm font-medium text-stone-700">Send reminder email</span>
                                </label>
                                {editForm.sendReminder && (
                                    <select
                                        value={editForm.reminderMinutesBefore || 30}
                                        onChange={(e) => setEditForm((f) => ({ ...f, reminderMinutesBefore: Number(e.target.value) }))}
                                        className="border border-stone-200 rounded-lg px-3 py-2 text-stone-800 text-sm"
                                    >
                                        {REMINDER_OPTIONS.map((opt) => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                )}
                            </div>
                        </div>
                        <div className="sticky bottom-0 flex flex-wrap gap-2 p-4 border-t border-stone-200 bg-white">
                            <button
                                type="button"
                                onClick={() => setSelected(null)}
                                className="flex-1 min-w-[80px] py-2.5 rounded-lg border border-stone-200 text-stone-700 font-medium"
                            >
                                Close
                            </button>
                            <button
                                type="button"
                                onClick={() => openDeleteModal(selected)}
                                disabled={saving}
                                className="py-2.5 px-4 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 font-medium disabled:opacity-50"
                            >
                                Delete
                            </button>
                            <button
                                type="button"
                                onClick={handleSave}
                                disabled={saving}
                                className="flex-1 min-w-[80px] py-2.5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white font-medium disabled:opacity-50"
                            >
                                {saving ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Create reservation modal (owner books for customer) */}
            {showCreateModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/60 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-xl shadow-2xl border border-stone-200 max-w-lg w-full max-h-[90vh] flex flex-col overflow-hidden">
                        <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 border-b border-stone-200 bg-stone-50">
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500">
                                Book table for customer
                            </p>
                            <button
                                type="button"
                                onClick={() => setShowCreateModal(false)}
                                className="w-7 h-7 rounded-full border border-stone-300 flex items-center justify-center text-stone-600 hover:bg-stone-100"
                            >
                                ×
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-4 space-y-3">
                            {createError && (
                                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
                                    {createError}
                                </div>
                            )}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1">Customer name *</label>
                                    <input
                                        type="text"
                                        value={createForm.customerName}
                                        onChange={(e) => setCreateForm((f) => ({ ...f, customerName: e.target.value }))}
                                        className="w-full border border-stone-200 rounded-lg px-3 py-2 text-stone-800"
                                        placeholder="Full name"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1">Email *</label>
                                    <input
                                        type="email"
                                        value={createForm.email}
                                        onChange={(e) => setCreateForm((f) => ({ ...f, email: e.target.value }))}
                                        className="w-full border border-stone-200 rounded-lg px-3 py-2 text-stone-800"
                                        placeholder="email@example.com"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1">Contact number *</label>
                                <input
                                    type="tel"
                                    value={createForm.contactNumber}
                                    onChange={(e) => {
                                        const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, 10);
                                        setCreateForm((f) => ({ ...f, contactNumber: digitsOnly }));
                                    }}
                                    className="w-full border border-stone-200 rounded-lg px-3 py-2 text-stone-800"
                                    placeholder="10-digit mobile"
                                    maxLength={10}
                                    inputMode="numeric"
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1">Number of guests *</label>
                                <input
                                    type="number"
                                    min={1}
                                    max={100}
                                    value={createForm.numberOfPeople}
                                    onChange={(e) => setCreateForm((f) => ({ ...f, numberOfPeople: e.target.value }))}
                                    className="w-full border border-stone-200 rounded-lg px-3 py-2 text-stone-800"
                                />
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1">Date *</label>
                                    <input
                                        type="date"
                                        value={createForm.reservationDate}
                                        onChange={(e) => setCreateForm((f) => ({ ...f, reservationDate: e.target.value }))}
                                        min={new Date().toISOString().split('T')[0]}
                                        className="w-full border border-stone-200 rounded-lg px-3 py-2 text-stone-800"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1">Time *</label>
                                    <input
                                        type="time"
                                        value={createForm.reservationTime}
                                        onChange={(e) => setCreateForm((f) => ({ ...f, reservationTime: e.target.value }))}
                                        className="w-full border border-stone-200 rounded-lg px-3 py-2 text-stone-800"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1">Occasion</label>
                                <select
                                    value={createForm.occasion}
                                    onChange={(e) => setCreateForm((f) => ({ ...f, occasion: e.target.value }))}
                                    className="w-full border border-stone-200 rounded-lg px-3 py-2 text-stone-800 bg-white"
                                >
                                    {OCCASIONS.map((opt) => (
                                        <option key={opt || 'none'} value={opt}>{opt || '— Select —'}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1">Message / notes</label>
                                <textarea
                                    value={createForm.message}
                                    onChange={(e) => setCreateForm((f) => ({ ...f, message: e.target.value }))}
                                    rows={1}
                                    className="w-full border border-stone-200 rounded-lg px-3 py-2 text-stone-800"
                                    placeholder="e.g. booking for anniversary"
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1">Special instructions</label>
                                <textarea
                                    value={createForm.specialInstructions}
                                    onChange={(e) => setCreateForm((f) => ({ ...f, specialInstructions: e.target.value }))}
                                    rows={1}
                                    className="w-full border border-stone-200 rounded-lg px-3 py-2 text-stone-800"
                                    placeholder="Dietary, high chair, etc."
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1">Pre-order (optional)</label>
                                <div className="border border-stone-200 rounded-lg overflow-hidden max-h-32 overflow-y-auto">
                                    <ul className="divide-y divide-stone-100">
                                        {menuItems.slice(0, 30).map((item) => (
                                            <li key={item._id} className="flex items-center justify-between px-3 py-1.5 hover:bg-stone-50">
                                                <span className="text-xs text-stone-700 truncate flex-1">{item.itemName} — ₹{item.itemPrice}</span>
                                                <button type="button" onClick={() => addCreateMenuItem(item)} className="text-orange-600 text-xs font-medium">Add</button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                {createForm.selectedItems.length > 0 && (
                                    <div className="mt-2 p-2 bg-stone-50 rounded-lg border border-stone-200">
                                        {createForm.selectedItems.map((i) => (
                                            <div key={i.menuItemId} className="flex items-center justify-between text-xs py-0.5">
                                                <span className="text-stone-700">{i.name} × {i.quantity}</span>
                                                <div className="flex items-center gap-0.5">
                                                    <button type="button" onClick={() => updateCreateItemQty(i.menuItemId, -1)} className="w-5 h-5 rounded border border-stone-300">−</button>
                                                    <button type="button" onClick={() => updateCreateItemQty(i.menuItemId, 1)} className="w-5 h-5 rounded border border-stone-300">+</button>
                                                    <button type="button" onClick={() => removeCreateMenuItem(i.menuItemId)} className="text-red-600 ml-1">Remove</button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1">Assign table now</label>
                                    <select
                                        value={createForm.assignedTableId}
                                        onChange={(e) => setCreateForm((f) => ({ ...f, assignedTableId: e.target.value }))}
                                        className="w-full border border-stone-200 rounded-lg px-3 py-2 text-stone-800"
                                    >
                                        <option value="">— None —</option>
                                        {(() => {
                                            const statusMap = getTableStatusForSlot(tables, reservations, createForm.reservationDate, createForm.reservationTime, null);
                                            return tables.map((t) => (
                                                <option key={t._id} value={t._id}>
                                                    {t.tableName || t.tableNumber} — {statusMap[t._id] ?? (t.status || 'Available')}
                                                </option>
                                            ));
                                        })()}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1">Status</label>
                                    <select
                                        value={createForm.status}
                                        onChange={(e) => setCreateForm((f) => ({ ...f, status: e.target.value }))}
                                        className="w-full border border-stone-200 rounded-lg px-3 py-2 text-stone-800"
                                    >
                                        {STATUS_OPTIONS.map((opt) => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="flex flex-wrap items-center gap-3">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={!!createForm.sendReminder}
                                        onChange={(e) => setCreateForm((f) => ({ ...f, sendReminder: e.target.checked }))}
                                        className="rounded border-stone-300 text-orange-600 focus:ring-orange-500"
                                    />
                                    <span className="text-sm font-medium text-stone-700">Send reminder email</span>
                                </label>
                                {createForm.sendReminder && (
                                    <select
                                        value={createForm.reminderMinutesBefore || 30}
                                        onChange={(e) => setCreateForm((f) => ({ ...f, reminderMinutesBefore: Number(e.target.value) }))}
                                        className="border border-stone-200 rounded-lg px-3 py-2 text-stone-800 text-sm"
                                    >
                                        {REMINDER_OPTIONS.map((opt) => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                )}
                            </div>
                        </div>
                        <div className="sticky bottom-0 flex gap-2 p-4 border-t border-stone-200 bg-white">
                            <button
                                type="button"
                                onClick={() => setShowCreateModal(false)}
                                className="flex-1 py-2.5 rounded-lg border border-stone-200 text-stone-700 font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleCreateSubmit}
                                disabled={createSaving}
                                className="flex-1 py-2.5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white font-medium disabled:opacity-50"
                            >
                                {createSaving ? 'Creating...' : 'Create reservation'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DineInSection;
